import { useMemo } from 'react'
import { useTranslations } from '@/hooks/useTranslations'
import { useAgentConfigurations } from '@/hooks/useAgentConfigurations'
import { useAgentRuntime, useAgentStore } from '@/context/AgentProvider'

import { AgentPreferencesPanel } from '@/components/AgentPreferences'
import type { AgentPreferenceItem } from '@/components/AgentPreferences'
import type { AgentConfiguration } from '@multiplayer-app/ai-agent-types'

function toPreferences(configs: AgentConfiguration[]): AgentPreferenceItem[] {
  return configs.map((agent) => {
    const tools = (agent.tools ?? []).reduce<Record<string, boolean>>((acc, tool) => {
      if (tool.userConfigurable) {
        acc[tool.title] = !tool.disabledByDefault
      }
      return acc
    }, {})
    return {
      agentName: agent.name,
      tools
    }
  })
}

const AgentSettingsPanel = () => {
  const t = useTranslations()
  const runtime = useAgentRuntime()
  const contextKey = useAgentStore((state) => state.contextKey)
  const { data, isLoading, isError } = useAgentConfigurations(contextKey)
  const preferences = useMemo(() => toPreferences(data ?? []), [data])

  const canPersist =
    Boolean(runtime.transport.upsertAgentPreferences) && Boolean(runtime.transport.upsertAgentPreferencesBatch)

  if (isLoading) {
    return <>{t.agentSettingsLoading}</>
  }

  if (isError) {
    return <>{t.agentSettingsError}</>
  }

  return <AgentPreferencesPanel preferences={preferences} disabled={!canPersist} />
}

export default AgentSettingsPanel
